import { useState, useEffect, useRef } from 'react'
import type { SummaryInput, SummaryResult, SavedSummary } from '@creanizer/types'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Alert, AlertDescription, AlertTitle } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { AlertCircle, Save, FileText, Zap } from 'lucide-react'
import { useSummaryStore } from '../../../../stores/summaryStore'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useSceneStore } from '../../../../stores/sceneStore'
import { usePlotStore } from '../../../../stores/plotStore'
import { SummaryForm } from './SummaryForm'
import { SummaryResult as SummaryResultDisplay } from './SummaryResult'
import { SavedSummariesList } from './SavedSummariesList'
import { validateInput } from './utils/validation'
import { ErrorBoundary } from '../../../../components/ErrorBoundary'

function SummaryGeneratorContent() {
  const { isApiKeySet } = useSettingsStore()
  const { summaries, addSummary, removeSummary, updateSummary } = useSummaryStore()
  const { currentWorld } = useWorldBuilderStore()
  const { scenes } = useSceneStore()
  const { savedPlots: plots } = usePlotStore()
  const generator = useGenerator('summary')
  const streamGenerator = useStreamGenerator('summary')
  
  const [activeTab, setActiveTab] = useState('generate')
  const [input, setInput] = useState<SummaryInput>({
    inputType: 'text',
    text: '',
    targetLength: 5000,
    summaryStyle: 'narrative',
    preserveElements: {
      dialogue: true,
      actions: true,
      descriptions: true,
      emotions: true,
      foreshadowing: true,
      keyPoints: true
    },
    textType: 'auto',
    useProMode: false,
    creativityLevel: 5,
    compressionRatio: 0.3
  })
  
  const [results, setResults] = useState<SummaryResult[]>([])
  const [error, setError] = useState<string>('')
  const [charCount, setCharCount] = useState(0)
  const [useStreaming, setUseStreaming] = useState<boolean>(true)
  const resultRef = useRef<HTMLDivElement>(null)
  
  // 文字数計算
  useEffect(() => {
    const text = input.inputType === 'text' ? input.text : input.fileContent
    setCharCount(text?.length || 0)
  }, [input.text, input.fileContent, input.inputType])
  
  // 圧縮率の自動計算
  useEffect(() => {
    if (charCount > 0) {
      const ratio = input.targetLength / charCount
      setInput(prev => ({
        ...prev,
        compressionRatio: Math.min(0.9, Math.max(0.1, ratio))
      }))
    }
  }, [input.targetLength, charCount])
  
  // 要約生成
  const handleGenerate = async () => {
    if (!isApiKeySet()) {
      setError('APIキーが設定されていません。設定ページから設定してください。')
      return
    }
    
    // バリデーション
    if (!validateInput(input)) {
      setError('テキストを入力してください')
      return
    }
    
    if (charCount < 100) {
      setError('要約するには短すぎます（最低100文字）')
      return
    }
    
    if (charCount < input.targetLength) {
      setError('元のテキストが目標文字数より短いです')
      return
    }
    
    setError('')
    setResults([])
    
    try {
      // ストリーミングモードの選択（5000文字以上の場合）
      if (useStreaming && charCount > 5000) {
        await streamGenerator.generateStream(input, {
          onChunk: (chunk) => {
            console.log('Received chunk:', chunk.length)
          },
          onComplete: (result) => {
            if (result !== null && result !== undefined) {
              // 結果が有効な場合のみ処理
              if (typeof result === 'object' && 'summary' in result) {
                handleResult(result as SummaryResult)
              } else {
                console.error('Invalid streaming result structure:', result)
                setError('ストリーミング処理の結果が不正です')
              }
            } else {
              console.error('Null or undefined result from streaming')
              setError('結果の生成に失敗しました')
            }
          },
          onError: (error) => {
            console.error('Streaming error:', error)
            setError(error.message || '要約の生成中にエラーが発生しました')
            
            // ストリーミングエラーの場合は通常モードにフォールバック
            if (error.message?.includes('ストリーミング')) {
              setUseStreaming(false)
              console.log('Falling back to non-streaming mode')
            }
          }
        })
      } else {
        const response = await generator.mutateAsync(input)
        
        // レスポンスの検証
        if (!response || !response.result) {
          throw new Error('生成結果が空です')
        }
        
        handleResult(response.result as SummaryResult)
      }
      
      // 結果へ自動スクロール
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    } catch (err: any) {
      console.error('Generation error:', err)
      const errorMessage = err.message || '要約の生成に失敗しました'
      setError(errorMessage)
      
      // APIキー関連のエラーの場合は特別な処理
      if (errorMessage.includes('API') || errorMessage.includes('401') || errorMessage.includes('403')) {
        setError('APIキーが無効です。設定を確認してください。')
      } else if (errorMessage.includes('429') || errorMessage.includes('quota')) {
        setError('API利用制限に達しました。しばらく待ってからお試しください。')
      } else if (errorMessage.includes('ネットワーク') || errorMessage.includes('network')) {
        setError('ネットワークエラーが発生しました。接続を確認してください。')
      }
    }
  }
  
  // 結果処理
  const handleResult = (result: SummaryResult) => {
    // 結果の型検証
    if (!result || typeof result !== 'object') {
      console.error('Invalid result structure:', result)
      setError('生成結果の形式が不正です')
      return
    }
    
    // summaryフィールドの検証
    if (!result.summary || typeof result.summary !== 'string') {
      console.error('Invalid summary field:', result.summary)
      setError('要約テキストが正しく生成されませんでした')
      return
    }
    
    // metadataの検証
    if (!result.metadata || typeof result.metadata !== 'object') {
      console.warn('Metadata is missing, using defaults')
      result.metadata = {
        originalLength: charCount,
        summaryLength: result.summary.length,
        compressionRatio: result.summary.length / charCount,
        textType: input.textType || 'auto',
        generatedAt: new Date().toISOString()
      }
    }
    
    setResults([result])
  }
  
  // 要約保存
  const handleSave = (result: SummaryResult) => {
    const savedSummary: SavedSummary = {
      id: `summary_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      title: generateTitle(result),
      input,
      result,
      originalText: input.text || input.fileContent,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      projectId: currentWorld?.id
    }
    
    addSummary(savedSummary)
  }
  
  // タイトル生成
  const generateTitle = (result: SummaryResult): string => {
    const preview = result.summary.slice(0, 50)
    const date = new Date().toLocaleDateString()
    return `要約 - ${preview}... (${date})`
  }
  
  // 保存済み要約の削除
  const handleDelete = (id: string) => {
    removeSummary(id)
  }
  
  // 保存済み要約の読み込み
  const handleLoad = (summary: SavedSummary) => {
    setInput(summary.input)
    setResults([summary.result])
    setActiveTab('generate')
  }
  
  // 保存済みチェック
  const isSaved = (result: SummaryResult) => {
    if (!result) return false
    return summaries.some(s => 
      s?.result?.summary === result.summary
    )
  }
  
  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-5 w-5" />
          <h2 className="text-xl font-semibold">テキスト要約ツール</h2>
        </div>
        <p className="text-muted-foreground mb-4">
          小説やTRPGログなどの長文を、要点を抑えてコンパクトに要約します
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="generate">生成</TabsTrigger>
          <TabsTrigger value="saved">
            保存済み ({summaries.length}/50)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="space-y-6">
          {/* 入力フォーム */}
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">要約設定</h3>
            
            <SummaryForm 
              input={input}
              onChange={setInput}
              charCount={charCount}
            />
            
            {/* Proモード切り替え */}
            <div className="flex items-center space-x-2 mt-4">
              <Checkbox
                id="useProMode"
                checked={input.useProMode || false}
                onCheckedChange={(checked) => setInput({
                  ...input,
                  useProMode: checked === true
                })}
              />
              <Label htmlFor="useProMode" className="flex items-center gap-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Proモード（より高精度な要約）
              </Label>
            </div>

            {/* Gemini 3 Flash モード */}
            <div className="flex items-center space-x-2 mt-4">
              <Checkbox
                id="useGemini3FlashMode"
                checked={input.useGemini3FlashMode || false}
                onCheckedChange={(checked) => setInput({
                  ...input,
                  useGemini3FlashMode: checked === true
                })}
              />
              <Label htmlFor="useGemini3FlashMode" className="flex items-center gap-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Gemini 3 Flash モード（試験版）
              </Label>
            </div>

            {/* ストリーミングモード切り替え */}
            <div className="flex items-center space-x-2 mt-4">
              <Checkbox
                id="useStreaming"
                checked={useStreaming}
                onCheckedChange={(checked) => setUseStreaming(checked === true)}
              />
              <Label htmlFor="useStreaming" className="flex items-center gap-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                <Zap className="h-4 w-4" />
                ストリーミングを使用（リアルタイム生成・進捗表示）
              </Label>
            </div>
            
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                元の文字数: {charCount.toLocaleString()}文字
                {charCount > 0 && (
                  <> → 要約後: 約{input.targetLength.toLocaleString()}文字
                  （圧縮率: {Math.round(input.compressionRatio * 100)}%）</>
                )}
              </div>
              
              <Button
                onClick={handleGenerate}
                disabled={generator.isPending || streamGenerator.isStreaming}
                className="flex items-center gap-2"
              >
                <FileText className="h-4 w-4" />
                {generator.isPending || streamGenerator.isStreaming 
                  ? '生成中...' 
                  : '要約生成'}
              </Button>
            </div>
          </div>
          
          {/* ストリーミング進捗表示 */}
          {streamGenerator.isStreaming && (
            <div className="card">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>処理中...</span>
                  <span>{Math.round(streamGenerator.progress || 0)}%</span>
                </div>
                <Progress
                  value={streamGenerator.progress || 0}
                  className="w-full"
                />
                {streamGenerator.partialContent && (
                  <div className="mt-4 p-4 bg-muted rounded max-h-48 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap">
                      {typeof streamGenerator.partialContent === 'string' 
                        ? streamGenerator.partialContent.slice(-500)
                        : JSON.stringify(streamGenerator.partialContent).slice(-500)}
                      {(typeof streamGenerator.partialContent === 'string' 
                        ? streamGenerator.partialContent.length 
                        : JSON.stringify(streamGenerator.partialContent).length) > 500 && '...'}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* エラー表示 */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>エラー</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {/* 結果表示 */}
          {results.length > 0 && (
            <div className="card" ref={resultRef}>
              <div className="flex flex-row items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">生成結果</h3>
                {!isSaved(results[0]) && (
                  <Button
                    onClick={() => handleSave(results[0])}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    保存
                  </Button>
                )}
              </div>
              
              {results[0] && (
                <SummaryResultDisplay 
                  result={results[0]}
                  input={input}
                />
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="saved" className="space-y-4">
          <SavedSummariesList
            summaries={summaries}
            onLoad={handleLoad}
            onDelete={handleDelete}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// Error Boundaryでラップしてエクスポート
export function SummaryGenerator() {
  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        console.error('SummaryGenerator error:', error)
        console.error('Error info:', errorInfo)
      }}
    >
      <SummaryGeneratorContent />
    </ErrorBoundary>
  )
}